
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SpacerBlockConfiguration } from "@/types/block-configurations";
import { ColorPicker, ResponsiveControls } from "../shared";

interface SpacerBlockPropertiesProps {
  config: SpacerBlockConfiguration;
  onChange: (config: SpacerBlockConfiguration) => void;
}

export function SpacerBlockProperties({ config, onChange }: SpacerBlockPropertiesProps) {
  const updateField = <K extends keyof SpacerBlockConfiguration>(
    field: K,
    value: SpacerBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div className="space-y-3">
      {/* Visual Height Indicator */}
      <div>
        <Label className="text-xs font-medium mb-1 block">Visual Preview</Label>
        <div className="relative bg-muted rounded-md p-4 flex items-center justify-center">
          <div
            style={{
              width: '100%',
              height: `${Math.min(config.height, 100)}px`,
              backgroundColor: 'transparent',
              border: '1px dashed hsl(var(--border))',
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs text-muted-foreground bg-background px-2 py-1 rounded">
              {config.height}px
            </span>
          </div>
        </div>
      </div>

      {/* Quick Presets */}
      <div>
        <Label className="text-xs font-medium mb-1 block">Quick Presets</Label>
        <div className="grid grid-cols-3 gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => updateField('height', 10)}
          >
            Small (10px)
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => updateField('height', 20)}
          >
            Medium (20px)
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => updateField('height', 40)}
          >
            Large (40px)
          </Button>
        </div>
      </div>

      {/* Height Slider */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs font-medium">Height</Label>
          <span className="text-xs text-muted-foreground">{config.height}px</span>
        </div>
        <Slider
          value={[config.height]}
          onValueChange={([value]) => updateField('height', value)}
          min={0}
          max={200}
          step={1}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Adds vertical whitespace between sections
        </p>
      </div>

      {/* Background Color */}
      <div>
        <Label className="text-xs font-medium mb-1 block">Background Color</Label>
        <ColorPicker
          value={config.backgroundColor === 'transparent' ? undefined : config.backgroundColor}
          onChange={(color) => updateField('backgroundColor', color ?? 'transparent')}
        />
      </div>

      {/* Collapsible Sections */}
      <Accordion type="multiple" className="w-full">

        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
